import { MouseEvent } from "react";
import toast, { Toast } from "react-hot-toast";
import { makeStyles } from "tss-react/mui";
import { IToastButtonProps } from "@miplatform/types";
import {
  mdiAlertCircleOutline,
  mdiAlertOutline,
  mdiCheckboxMarkedCircleOutline,
  mdiClose,
  mdiInformationOutline,
} from "@mdi/js";
import Icon from "@mdi/react";
import { Box, Button, IconButton, Typography, keyframes } from "@mui/material";

export enum TOAST_TYPES {
  SUCCESS = "success",
  ERROR = "error",
  WARNING = "warning",
  INFO = "info",
}

interface ToastConfig {
  icon: string;
  testId: string;
}

const TOAST_CONFIG: Record<TOAST_TYPES, ToastConfig> = {
  [TOAST_TYPES.SUCCESS]: {
    icon: mdiCheckboxMarkedCircleOutline,
    testId: "success-toast",
  },
  [TOAST_TYPES.WARNING]: {
    icon: mdiAlertOutline,
    testId: "warning-toast",
  },
  [TOAST_TYPES.INFO]: {
    icon: mdiInformationOutline,
    testId: "info-toast",
  },
  [TOAST_TYPES.ERROR]: {
    icon: mdiAlertCircleOutline,
    testId: "error-toast",
  },
};

export function MiToast({
  type = TOAST_TYPES.INFO,
  text = "",
  subtext = "",
  buttonProps = null,
  t,
}: Readonly<{
  type: TOAST_TYPES;
  text?: string;
  subtext?: string | React.ReactNode;
  buttonProps?: IToastButtonProps | null;
  t: Toast;
}>) {
  const { classes } = useStyles();
  const { icon, testId } = TOAST_CONFIG[type];

  const dismissToast = () => {
    toast.dismiss(t.id);
  };

  const handleButtonClick = (e: MouseEvent<HTMLButtonElement>) => {
    buttonProps?.onClick?.(e);
    toast.dismiss(t.id);
  };

  return (
    <Box
      data-testid={testId}
      className={`${classes[type]} ${classes.toast}`}
      sx={{
        animation: t.visible
          ? `${customEnter} 0.25s ease-in-out`
          : `${customExit} 0.25s ease-in-out forwards`,
      }}
    >
      <Box className={classes.toastContent}>
        <Icon size={1} path={icon} className={classes.toastIcon} />
        <Box className={classes.toastMessage}>
          <Typography fontWeight={600} flexWrap="wrap">
            {text}
          </Typography>
          {subtext && <Typography fontSize={12}>{subtext}</Typography>}
        </Box>
      </Box>
      <Box display="flex" alignItems="center" gap={2}>
        {buttonProps && (
          <Button
            className={classes.toastButton}
            {...buttonProps}
            onClick={handleButtonClick}
          >
            {buttonProps.title}
          </Button>
        )}
        <IconButton
          className={classes.dismissButton}
          size="small"
          onClick={dismissToast}
        >
          <Icon size={1} path={mdiClose} />
        </IconButton>
      </Box>
    </Box>
  );
}

const customEnter = keyframes`
    0% {
        opacity: 0;
    }
    100% {
        opacity: 1;
    }

`;

const customExit = keyframes`
    0% {
        opacity: 1;
    }
    100% {
        opacity: 0;
    }
`;

const useStyles = makeStyles()((theme) => {
  return {
    toast: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      backgroundColor: theme.palette.common.black,
      color: theme.palette.common.white,
      position: "relative",
      width: "70vw",
      maxWidth: "800px",
      padding: theme.spacing(2, 3),
      borderRadius: 4,
      gap: theme.spacing(3),
      boxShadow: `0 0px 6px -2px ${theme.palette.common.black}`,
      wordBreak: "break-word",
    },
    success: {
      backgroundColor: theme.palette.success.main,
      color: theme.palette.common.white,
    },
    warning: {
      backgroundColor: theme.palette.warning.main,
      color: theme.palette.common.white,
    },
    error: {
      backgroundColor: theme.palette.error.main,
      color: theme.palette.common.white,
    },
    info: {
      backgroundColor: theme.palette.info.main,
      color: theme.palette.common.white,
    },
    toastContent: {
      display: "flex",
      alignItems: "center",
      gap: theme.spacing(3),
    },
    toastIcon: {
      flexShrink: 0,
    },
    toastMessage: {
      display: "flex",
      flexDirection: "column",
      flexShrink: 1,
    },
    toastButton: {
      color: theme.palette.common.white,
      border: `1px solid ${theme.palette.common.white}`,
      textTransform: "none",
      fontSize: 14,
      minWidth: "fit-content",

      "&:hover": {
        backgroundColor: theme.palette.overlay.main,
      },
    },
    dismissButton: {
      color: theme.palette.common.white,

      "&:hover": {
        backgroundColor: theme.palette.overlay.main,
      },
    },
  };
});
